import { studyQueryKeys } from '@/config/queryKeys';
import { apiClient } from '@/lib/api-client';
import type { PaginatedProteoformSampleSummariesResponse, ProteoformSampleSummarySearchRequest } from '@/types';

/**
 * Searches per-sample proteoform summaries for one assay within a study.
 *
 * Replaces the removed `sample_proteoform_counts/search`. One row per sample carrying
 * `num_proteoform_groups` / `num_ptms` / `num_isoforms`, and the envelope also carries
 * study-wide totals for those three plus `num_samples` — which is where study-level counts
 * come from now that they were dropped from `StudyPublicGetOne` and `SampleWithNamesPublic`.
 *
 * `assay_id` is required: the counts are only meaningful against a single assay panel, which
 * is why this is scoped per-assay rather than per-study. Do NOT call it once per assay merely
 * to render a study header — the underlying fact table is large and no screen needs that.
 *
 * The counts come from a LEFT-JOINed CTE, so unlike the old shape they are genuinely sortable
 * and filterable rather than merged in afterwards.
 */
export async function searchProteoformSampleSummaries(
  orgId: string,
  studyId: string,
  request: ProteoformSampleSummarySearchRequest,
): Promise<PaginatedProteoformSampleSummariesResponse> {
  const response = await apiClient.post<PaginatedProteoformSampleSummariesResponse>(
    studyQueryKeys.endpoints.proteoformSampleSummariesSearch(orgId, studyId),
    request,
    { expectJson: true },
  );

  if (response === null) {
    throw new Error(`No proteoform sample summaries returned for study ${studyId}`);
  }

  return response;
}
